import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MoreHorizontal, Pencil, Plus, Receipt, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StatusChip } from './StatusChip';
import { CreatePromoCodeDialog } from './CreatePromoCodeDialog';
import { EditPromoCodeDialog } from './EditPromoCodeDialog';
import { PromoCodeRedemptions } from './PromoCodeRedemptions';
import {
  expirePromoCode,
  isPlatformAdmin,
  listPromoCodes,
  promoErrorMessage,
  type PromoCode,
} from '@/lib/promoApi';
import { formatPromoDate } from '@/lib/promoDates';

type Props = {
  appid: string;
  tenantName?: string;
  walletAddress: string;
};

const COLUMNS = ['Code', 'Scope', 'Discount', 'Status', 'Redemptions', 'Expires', 'Created', ''];

export const PromoCodesPanel: React.FC<Props> = ({ appid, tenantName, walletAddress }) => {
  const isAdmin = isPlatformAdmin(walletAddress);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [viewingRedemptions, setViewingRedemptions] = useState<PromoCode | null>(null);
  const [expiring, setExpiring] = useState<PromoCode | null>(null);
  const [expireBusy, setExpireBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!appid) return;
    setLoading(true);
    setError(null);
    try {
      // Tenant codes for the active appid; admins additionally see global codes.
      const requests = [listPromoCodes(walletAddress, { appid })];
      if (isAdmin) requests.push(listPromoCodes(walletAddress, { scope: 'global' }));
      const results = await Promise.all(requests);
      const merged = ([] as PromoCode[]).concat(...results);
      merged.sort((a, b) => b.createdAt - a.createdAt);
      setCodes(merged);
    } catch (err) {
      setError(promoErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appid, walletAddress, isAdmin]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleExpire = async () => {
    if (!expiring) return;
    setExpireBusy(true);
    try {
      await expirePromoCode(walletAddress, expiring.promoCodeId);
      toast.success(`Code ${expiring.code} disabled`);
      setExpiring(null);
      await reload();
    } catch (err) {
      toast.error(promoErrorMessage(err));
    } finally {
      setExpireBusy(false);
    }
  };

  return (
    <div className='flex flex-col gap-4' data-testid='promo-codes-panel'>
      <div className='flex items-center justify-between gap-3'>
        <div>
          <h2 className='text-sm font-semibold' style={{ color: 'var(--pp-text)' }}>
            Promo codes
          </h2>
          <p className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
            Percent-off codes for {tenantName ?? appid}. A 100% code settles a $0 session and still fires the
            signed webhook.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid='promo-new-code'
          className='h-8 text-xs font-semibold gap-1.5 flex-shrink-0'
          style={{ background: 'var(--pp-action)', color: '#fff' }}
        >
          <Plus size={14} />
          New code
        </Button>
      </div>

      {loading ? (
        <div className='flex items-center justify-center gap-2 py-16 text-sm' style={{ color: 'var(--pp-text-subtle)' }}>
          <Loader2 size={16} className='animate-spin' />
          Loading promo codes…
        </div>
      ) : error ? (
        <div className='rounded-lg border p-6 text-center text-sm flex flex-col items-center gap-3'
          style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-danger)' }}>
          {error}
          <Button variant='outline' className='h-8 text-xs' onClick={() => void reload()}>Retry</Button>
        </div>
      ) : codes.length === 0 ? (
        <div className='rounded-lg border p-10 text-center flex flex-col items-center gap-3'
          style={{ borderColor: 'var(--pp-border)', background: 'var(--pp-surface)' }}>
          <p className='text-sm' style={{ color: 'var(--pp-text-muted)' }}>No promo codes yet.</p>
          <Button
            onClick={() => setCreateOpen(true)}
            className='h-8 text-xs font-semibold gap-1.5'
            style={{ background: 'var(--pp-action)', color: '#fff' }}
          >
            <Plus size={14} />
            Create your first code
          </Button>
        </div>
      ) : (
        <div className='overflow-x-auto rounded-lg border' style={{ borderColor: 'var(--pp-border)' }}>
          <table className='w-full text-sm'>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--pp-border)', background: 'var(--pp-subtle)' }}>
                {COLUMNS.map((h, idx) => (
                  <th
                    key={h || `col-${idx}`}
                    className='text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wide'
                    style={{ color: 'var(--pp-text-subtle)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {codes.map((code, i) => (
                <tr
                  key={code.promoCodeId}
                  data-testid='promo-code-row'
                  data-code={code.code}
                  style={{
                    borderBottom: i < codes.length - 1 ? '1px solid var(--pp-border)' : 'none',
                    background: i % 2 === 0 ? 'var(--pp-surface)' : 'var(--pp-bg)',
                  }}
                >
                  <td className='px-4 py-2.5' style={{ fontFamily: 'var(--pp-mono)', fontWeight: 600, color: 'var(--pp-text)' }}>
                    {code.code}
                  </td>
                  <td className='px-4 py-2.5'>
                    <span className='text-xs font-medium uppercase' style={{ color: 'var(--pp-text-muted)' }}>
                      {code.scope === 'global' ? 'Global' : 'Tenant'}
                    </span>
                  </td>
                  <td className='px-4 py-2.5 tabular-nums' style={{ color: 'var(--pp-text)', fontWeight: 500 }}>
                    {code.percentOff}%
                  </td>
                  <td className='px-4 py-2.5'>
                    <StatusChip status={code.status} />
                  </td>
                  <td className='px-4 py-2.5 tabular-nums' style={{ color: 'var(--pp-text-muted)' }}>
                    {code.redemptionCount}
                    {code.maxRedemptions ? ` / ${code.maxRedemptions}` : ' / ∞'}
                  </td>
                  <td className='px-4 py-2.5' style={{ fontSize: '0.72rem', color: 'var(--pp-text-subtle)' }}>
                    {formatPromoDate(code.expiresAt)}
                  </td>
                  <td className='px-4 py-2.5' style={{ fontSize: '0.72rem', color: 'var(--pp-text-subtle)' }}>
                    {formatPromoDate(code.createdAt)}
                  </td>
                  <td className='px-4 py-2.5 text-right'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className='h-7 w-7 rounded-md inline-flex items-center justify-center'
                          style={{ color: 'var(--pp-text-muted)' }}
                          aria-label={`Actions for ${code.code}`}
                          data-testid='promo-row-actions'
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end' className='w-48'>
                        <DropdownMenuItem onSelect={() => setEditing(code)} data-testid='promo-action-edit'>
                          <Pencil size={14} className='mr-2' />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setViewingRedemptions(code)} data-testid='promo-action-redemptions'>
                          <Receipt size={14} className='mr-2' />
                          View redemptions
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => setExpiring(code)}
                          disabled={code.status === 'disabled'}
                          data-testid='promo-action-expire'
                        >
                          <Ban size={14} className='mr-2' />
                          Expire (disable)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreatePromoCodeDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        walletAddress={walletAddress}
        appid={appid}
        isAdmin={isAdmin}
        onCreated={() => void reload()}
      />
      <EditPromoCodeDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        walletAddress={walletAddress}
        code={editing}
        onSaved={() => void reload()}
      />
      <PromoCodeRedemptions
        walletAddress={walletAddress}
        code={viewingRedemptions}
        onClose={() => setViewingRedemptions(null)}
      />

      <AlertDialog open={!!expiring} onOpenChange={(o) => !o && !expireBusy && setExpiring(null)}>
        <AlertDialogContent className='bg-[var(--pp-surface)] text-[var(--pp-text)]'>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable this code?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className='font-mono'>{expiring?.code}</span> will stop applying to new sessions. Existing
              settled redemptions are unaffected. You can re-enable it later from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={expireBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleExpire();
              }}
              disabled={expireBusy}
              data-testid='promo-expire-confirm'
              style={{ background: 'var(--pp-danger)', color: '#fff' }}
            >
              {expireBusy ? 'Disabling…' : 'Disable code'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromoCodesPanel;
